import React, { useState, useEffect } from 'react';
import { 
  X, 
  MapPin, 
  Phone, 
  Mail, 
  Send, 
  Award, 
  GraduationCap, 
  Globe, 
  Calendar, 
  Building2, 
  ExternalLink,
  ShieldCheck,
  CheckCircle2,
  MessageSquare,
  Lock
} from 'lucide-react';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { formatUzPhone, localizeLawyerPosition, localizeSpecialization, localizeLanguages } from '../../utils/helpers';

function InstagramIcon({ className = "w-4 h-4" }) {
  return (
    <svg 
      className={className} 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </svg>
  );
}

export default function LawyerDetailModal({ 
  isOpen, 
  onClose, 
  lawyer, 
  onBookAppointment, 
  onChatLawyer = null,
  onOpenSubscription = null 
}) {
  const { currentLang, t, localized } = useLanguage();
  const { isSubscribed } = useAuth();

  const [primaryOffice, setPrimaryOffice] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    api.get('/offices').then((res) => {
      const list = res?.offices || res?.data?.offices || [];
      if (list.length > 0) setPrimaryOffice(list[0]);
    }).catch(() => {});
  }, [isOpen]);

  if (!isOpen || !lawyer) return null;

  // Localized bio & texts
  const bioText = currentLang === 'uz' 
    ? (lawyer.bioUz || lawyer.bio || '') 
    : currentLang === 'ru' 
    ? (lawyer.bioRu || lawyer.bioUz || lawyer.bio || '') 
    : (lawyer.bioEn || lawyer.bioUz || lawyer.bio || '');

  const officeAddress = currentLang === 'uz'
    ? "Jizzax shahar, Islom Karimov ko'chasi, Turon Bank ro'parasi (Jizzax Advokatlari Markaziy Bosh Ofisi)"
    : currentLang === 'ru'
    ? "г. Джизак, улица Ислама Каримова, напротив Турон Банка (Центральный офис коллегии адвокатов Адвокаты Джизака)"
    : "Jizzakh city, Islam Karimov street, opposite Turon Bank (Jizzakh Advocates Central Headquarters)";

  const officeLat = primaryOffice?.latitude ?? 40.160331;
  const officeLng = primaryOffice?.longitude ?? 67.827246;
  const officeMapUrl = primaryOffice?.mapUrl || primaryOffice?.googleMapsUrl || `https://www.google.com/maps/search/?api=1&query=${officeLat},${officeLng}`;
  const dynamicOfficeAddress = (currentLang === 'ru' ? primaryOffice?.addressRu : currentLang === 'en' ? primaryOffice?.addressEn : primaryOffice?.addressUz) || primaryOffice?.address || officeAddress;

  const phoneDisplay = lawyer.phone ? formatUzPhone(lawyer.phone) : '+998(97) 325-07-70';
  const phoneClean = (lawyer.phone || '+998973250770').replace(/\D/g, '');

  const telegramUrl = lawyer.telegram 
    ? (lawyer.telegram.startsWith('http') ? lawyer.telegram : `https://t.me/${lawyer.telegram.replace('@', '')}`)
    : 'https://t.me/sevinchadvokat';

  const instagramUrl = lawyer.instagram
    ? (lawyer.instagram.startsWith('http') ? lawyer.instagram : `https://instagram.com/${lawyer.instagram.replace('@', '')}`)
    : 'https://instagram.com/sevinchadvokat';

  const emailDisplay = lawyer.email || '';

  const experienceText = currentLang === 'uz'
    ? `${lawyer.experienceYears} yillik oliy toifali sud va himoya tajribasi`
    : currentLang === 'ru'
    ? `${lawyer.experienceYears} лет высшей судебной и следственной практики`
    : `${lawyer.experienceYears} years of senior courtroom and defense experience`;

  // Parse education
  let educationList = [];
  try {
    if (lawyer.education) {
      educationList = lawyer.education.startsWith('[') ? JSON.parse(lawyer.education) : [lawyer.education];
    }
  } catch {
    educationList = [lawyer.education];
  }
  if (!educationList.length) {
    educationList = [
      currentLang === 'uz'
        ? "Toshkent davlat yuridik universiteti (TDYuU) — Magistratura"
        : currentLang === 'ru'
        ? "Ташкентский государственный юридический университет (ТГЮУ) — Магистратура"
        : "Tashkent State University of Law (TSUL) — LL.M. Master of Laws"
    ];
  }

  // Parse certifications
  let certsList = [];
  try {
    if (lawyer.certifications) {
      certsList = lawyer.certifications.startsWith('[') ? JSON.parse(lawyer.certifications) : [lawyer.certifications];
    }
  } catch {
    certsList = [lawyer.certifications];
  }
  if (!certsList.length) {
    certsList = [
      currentLang === 'uz'
        ? "O‘zbekiston Respublikasi Advokatlar Palatasi a’zosi va litsenziyasi"
        : currentLang === 'ru'
        ? "Член Палаты Адвокатов Республики Узбекистан (Государственная лицензия)"
        : "Chamber of Advocates of the Republic of Uzbekistan — Full Senior Bar License"
    ];
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/85 backdrop-blur-md flex items-center justify-center p-3 sm:p-4 animate-in fade-in duration-200">
      
      {/* Modal Dialog Card */}
      <div 
        className="relative bg-[#070c16] border border-[#c59b27]/50 rounded-2xl max-w-3xl w-full text-slate-200 shadow-2xl shadow-black overflow-hidden my-auto max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Gradient Ribbon */}
        <div className="h-1.5 w-full bg-gradient-to-r from-[#b38038] via-[#f3e5ab] to-[#c59b27]" />

        {/* Close Button */}
        <button
          onClick={onClose}
          type="button"
          className="absolute top-3.5 right-3.5 z-30 w-8 h-8 rounded-full bg-[#131d2e] hover:bg-[#1c2c46] border border-[#c59b27]/40 hover:border-[#c59b27] text-slate-300 hover:text-white flex items-center justify-center transition-all cursor-pointer shadow-md"
          aria-label="Yopish"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Scrollable Content Container */}
        <div className="overflow-y-auto p-5 sm:p-8 space-y-6">
          
          {/* Header Profile Area */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 pb-6 border-b border-[#141d2d]">
            {/* Crystal Clear Photo */}
            <div className="relative w-36 h-44 sm:w-40 sm:h-48 shrink-0 rounded-xl overflow-hidden border-2 border-[#c59b27]/60 shadow-xl bg-[#0a0f19]">
              <img
                src={lawyer.photoUrl || "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=600"}
                alt={lawyer.fullName}
                className="w-full h-full object-cover object-top filter brightness-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070c16]/70 via-transparent to-transparent pointer-events-none" />
            </div>

            {/* Title & Core Details */}
            <div className="flex-1 text-center sm:text-left space-y-2">
              <span className="inline-block px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#142033] text-[#c59b27] border border-[#c59b27]/40">
                {localizeLawyerPosition(lawyer.position || 'Advokat', currentLang).toUpperCase()}
              </span>

              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight">
                {lawyer.fullName}
              </h2>

              <p className="text-xs sm:text-sm font-semibold text-[#c59b27]">
                {localizeSpecialization(lawyer.specialization, currentLang)}
              </p>

              <div className="flex items-center justify-center sm:justify-start text-xs text-slate-300 space-x-2 pt-1">
                <Award className="w-4 h-4 text-[#c59b27] shrink-0" />
                <span className="font-medium text-amber-100">{experienceText}</span>
              </div>

              {lawyer.languages && (
                <div className="flex items-center justify-center sm:justify-start text-xs text-slate-400 space-x-2">
                  <Globe className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                  <span>{currentLang === 'uz' ? 'Muloqot tillari:' : currentLang === 'ru' ? 'Языки консультаций:' : 'Languages:'} {localizeLanguages(lawyer.languages, currentLang)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Workplace & Office Location (Iwxona qaerda) */}
          <div className="bg-[#0b1220] rounded-xl border border-[#1b2b46] p-4.5 space-y-2">
            <div className="flex items-center space-x-2 text-[#c59b27] text-xs font-bold uppercase tracking-wider">
              <Building2 className="w-4 h-4" />
              <span>{currentLang === 'uz' ? 'Ishxona va Qabul Ofisi' : currentLang === 'ru' ? 'Рабочий Офис и Место Приёма' : 'Workplace & Office Location'}</span>
            </div>
            <div className="flex items-start text-xs sm:text-sm text-slate-200">
              <MapPin className="w-4 h-4 text-[#c59b27] mr-2.5 shrink-0 mt-0.5" />
              <div className="flex-1">
                <span className="font-medium">{dynamicOfficeAddress}</span>
                <div className="mt-1">
                  <a
                    href={officeMapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] text-[#c59b27] hover:underline inline-flex items-center font-semibold mt-1"
                  >
                    {currentLang === 'en' ? 'View on Google Maps' : currentLang === 'ru' ? 'Посмотреть на Google Картах' : 'Google Xaritada ko‘rish'}
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Direct Contacts Grid (Telefon, Telegram, Instagram, Email) */}
          <div className="space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-300">
              {currentLang === 'uz' ? 'To‘g‘ridan-to‘g‘ri Aloqa Vositalari' : currentLang === 'ru' ? 'Прямые Контакты и Мессенджеры' : 'Direct Contacts & Social Channels'}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Phone */}
              {isSubscribed ? (
                <a
                  href={`tel:+${phoneClean}`}
                  className="flex items-center p-3 rounded-xl bg-[#090e18] border border-[#18263d] hover:border-[#c59b27] transition-all group"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#111c2e] border border-[#c59b27]/30 flex items-center justify-center text-[#c59b27] mr-3 shrink-0 group-hover:bg-[#c59b27] group-hover:text-black transition-colors">
                    <Phone className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                      {currentLang === 'uz' ? 'Telefon raqam' : currentLang === 'ru' ? 'Телефон' : 'Direct Phone'}
                    </span>
                    <span className="text-xs sm:text-sm font-mono font-bold text-slate-100 group-hover:text-[#c59b27] transition-colors truncate block">
                      {phoneDisplay}
                    </span>
                  </div>
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    if (onOpenSubscription) onOpenSubscription();
                  }}
                  className="flex items-center p-3 rounded-xl bg-[#090e18] border border-[#18263d] hover:border-[#c59b27]/80 transition-all group text-left cursor-pointer w-full"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#111c2e] border border-amber-500/30 flex items-center justify-center text-amber-400 mr-3 shrink-0 group-hover:bg-amber-500/20 transition-colors">
                    <Lock className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                        {currentLang === 'uz' ? 'Telefon raqam' : currentLang === 'ru' ? 'Телефон' : 'Direct Phone'}
                      </span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300 font-semibold flex items-center gap-1">
                        <Lock className="w-2.5 h-2.5" /> {currentLang === 'ru' ? 'Подписка' : currentLang === 'en' ? 'Subscription' : 'Obuna'}
                      </span>
                    </div>
                    <span className="text-xs sm:text-sm font-mono font-bold text-slate-400 group-hover:text-[#c59b27] transition-colors truncate block">
                      +998 (97) •••-••-••
                    </span>
                  </div>
                </button>
              )}

              {/* Telegram */}
              {isSubscribed ? (
                <a
                  href={telegramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center p-3 rounded-xl bg-[#090e18] border border-[#18263d] hover:border-[#229ed9] transition-all group"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#0e2133] border border-[#229ed9]/40 flex items-center justify-center text-[#229ed9] mr-3 shrink-0 group-hover:bg-[#229ed9] group-hover:text-white transition-colors">
                    <Send className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                      Telegram
                    </span>
                    <span className="text-xs font-medium text-slate-100 group-hover:text-[#229ed9] transition-colors truncate block flex items-center">
                      <span>{lawyer.telegram ? (lawyer.telegram.startsWith('@') ? lawyer.telegram : `@${lawyer.telegram.replace('https://t.me/', '')}`) : '@sevinchadvokat'}</span>
                      <ExternalLink className="w-3 h-3 ml-1 text-slate-500" />
                    </span>
                  </div>
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    if (onOpenSubscription) onOpenSubscription();
                  }}
                  className="flex items-center p-3 rounded-xl bg-[#090e18] border border-[#18263d] hover:border-[#229ed9]/80 transition-all group text-left cursor-pointer w-full"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#0e2133] border border-amber-500/30 flex items-center justify-center text-amber-400 mr-3 shrink-0 group-hover:bg-[#229ed9]/20 transition-colors">
                    <Lock className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                        Telegram
                      </span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300 font-semibold flex items-center gap-1">
                        <Lock className="w-2.5 h-2.5" /> {currentLang === 'ru' ? 'Подписка' : currentLang === 'en' ? 'Subscription' : 'Obuna'}
                      </span>
                    </div>
                    <span className="text-xs font-medium text-slate-400 group-hover:text-[#229ed9] transition-colors truncate block">
                      @••••••••••
                    </span>
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Biography / Description (in active language!) */}
          <div className="space-y-2 pt-2">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-300">
              {currentLang === 'uz' ? 'Kasbiy Malaka va Biografiya' : currentLang === 'ru' ? 'Квалификация и Биография' : 'Qualifications & Professional Background'}
            </h3>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-light bg-[#080d17] p-4 rounded-xl border border-[#141e30]">
              {bioText || "Jizzax Advokatlarining yetakchi advokati. Murakkab jinoyat, iqtisodiy va korporativ sud ishlarida fuqarolar hamda yirik korxonalarning qonuniy manfaatlarini ishonchli himoya qilib kelmoqda."}
            </p>
          </div>

          {/* Education & Bar Licenses */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
            {/* Education */}
            <div className="bg-[#080d17] p-4 rounded-xl border border-[#141e30] space-y-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#c59b27] flex items-center">
                <GraduationCap className="w-4 h-4 mr-1.5" />
                {currentLang === 'uz' ? 'Oliy Ma’lumot' : currentLang === 'ru' ? 'Образование' : 'Legal Education'}
              </span>
              <ul className="space-y-1.5 text-xs text-slate-300">
                {educationList.map((edu, idx) => (
                  <li key={idx} className="flex items-start">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#c59b27] mr-1.5 shrink-0 mt-0.5" />
                    <span>{edu}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Certifications */}
            <div className="bg-[#080d17] p-4 rounded-xl border border-[#141e30] space-y-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#c59b27] flex items-center">
                <ShieldCheck className="w-4 h-4 mr-1.5" />
                {currentLang === 'uz' ? 'Litsenziya va Sertifikatlar' : currentLang === 'ru' ? 'Лицензии и Аттестация' : 'Bar Admissions & Licensure'}
              </span>
              <ul className="space-y-1.5 text-xs text-slate-300">
                {certsList.map((cert, idx) => (
                  <li key={idx} className="flex items-start">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#c59b27] mr-1.5 shrink-0 mt-0.5" />
                    <span>{cert}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

        </div>

        {/* Modal Footer CTA */}
        <div className="p-4 sm:p-5 bg-[#05080e] border-t border-[#141d2d] flex flex-col sm:flex-row items-center justify-between gap-3">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto px-5 py-2.5 rounded border border-slate-700 hover:border-slate-500 text-xs font-semibold uppercase tracking-wider text-slate-300 hover:text-white transition-colors cursor-pointer"
          >
            {currentLang === 'uz' ? 'Yopish' : currentLang === 'ru' ? 'Закрыть' : 'Close'}
          </button>

          <div className="flex flex-col sm:flex-row items-center gap-2.5 w-full sm:w-auto">
            {onChatLawyer && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onChatLawyer(lawyer);
                }}
                className="w-full sm:w-auto px-5 py-2.5 rounded border border-[#c59b27]/80 hover:border-[#d4af37] bg-[#0e1728] hover:bg-[#15233c] text-[#c59b27] hover:text-[#d4af37] text-xs font-bold uppercase tracking-wider flex items-center justify-center transition-all cursor-pointer shadow-md"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                {currentLang === 'uz' ? 'Shaxsiy Chat' : currentLang === 'ru' ? 'Личный чат' : 'Direct Chat'}
                {!isSubscribed && <Lock className="w-3.5 h-3.5 ml-1.5 text-amber-400" />}
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                onClose();
                if (onBookAppointment) {
                  onBookAppointment(lawyer);
                }
              }}
              className="w-full sm:w-auto px-6 py-2.5 rounded bg-gradient-to-r from-[#b38038] to-[#c59b27] hover:from-[#c59b27] hover:to-[#d4af37] text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-[#b38038]/30 flex items-center justify-center transition-all cursor-pointer"
            >
              <Calendar className="w-4 h-4 mr-2" />
              {currentLang === 'uz' ? 'Qabulga yozilish' : currentLang === 'ru' ? 'Записаться к адвокату' : 'Book Consultation'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
